import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, User, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { fetchArticleBySlug, mediaUrl } from '../lib/blogApi';

const PLACEHOLDER = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_cargo-urgent/artifacts/dsr13j63_airfreight%20pic%202.png';
const SITE = 'https://cargo-urgent.preview.emergentagent.com';

const ArticleDetail = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchArticleBySlug(slug)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
    window.scrollTo(0, 0);
  }, [slug]);

  const fmt = (d) => { try { return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; } };

  if (loading) return <div className="min-h-screen bg-white pt-44 text-center text-[#4A4A4A]">Loading...</div>;
  if (error || !data) return (
    <div className="min-h-screen bg-white pt-44 text-center">
      <h1 className="text-3xl font-bold text-[#003057] mb-4">Article Not Found</h1>
      <Link to="/insights" className="text-[#0089D0] font-semibold">← Back to ATC Insights</Link>
    </div>
  );

  const a = data.article;
  const related = data.related || [];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${a.seo_title || a.title} | Aero Time Critical`}</title>
        <meta name="description" content={a.meta_description || a.summary} />
        <link rel="canonical" href={`${SITE}/insights/${a.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={a.seo_title || a.title} />
        <meta property="og:description" content={a.meta_description || a.summary} />
        {a.featured_image && <meta property="og:image" content={mediaUrl(a.featured_image)} />}
      </Helmet>

      {/* Hero */}
      <section className="relative pt-44 pb-16 bg-[#003057]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/insights" className="inline-flex items-center gap-2 text-[#00A3E0] font-semibold mb-6 hover:underline"><ArrowLeft size={16} /> Back to Insights</Link>
          <div className="flex items-center gap-3 text-sm text-[#C9A227] font-semibold mb-4">
            <span className="bg-[#C9A227]/20 px-3 py-1 rounded">{a.category}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">{a.title}</h1>
          <div className="flex flex-wrap items-center gap-6 text-gray-300 text-sm">
            <span className="flex items-center gap-2"><User size={16} /> {a.author}</span>
            <span className="flex items-center gap-2"><Calendar size={16} /> {fmt(a.publication_date)}</span>
          </div>
        </div>
      </section>

      {/* Featured image */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
        <img src={mediaUrl(a.featured_image) || PLACEHOLDER} alt={a.title} className="w-full h-[400px] object-cover rounded-lg shadow-xl" />
      </div>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {a.summary && <p className="text-xl text-[#003057] font-medium leading-relaxed mb-8 border-l-4 border-[#0089D0] pl-6">{a.summary}</p>}
          <div className="article-body text-[#4A4A4A] leading-relaxed" dangerouslySetInnerHTML={{ __html: a.content }} />

          {a.tags && a.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-10">
              {a.tags.map((t) => (
                <span key={t} className="flex items-center gap-1 bg-[#F7F9FB] text-[#003057] px-3 py-1 rounded-full text-sm"><Tag size={12} /> {t}</span>
              ))}
            </div>
          )}

          {/* Quote CTA */}
          <div className="mt-12 bg-[#003057] rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Need time-critical freight support?</h3>
            <p className="text-gray-300 mb-6">Our team is available 24/7 for AOG and urgent shipments worldwide.</p>
            <Link to="/quote"><button className="btn-primary" data-testid="article-quote-cta">Request a Quote</button></Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 bg-[#F7F9FB]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-[#003057] mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((r) => (
                <Link key={r.id} to={`/insights/${r.slug}`} className="block group">
                  <div className="bg-white border border-[#E5E9EF] rounded-lg overflow-hidden hover:shadow-xl transition-all">
                    <div className="h-40 overflow-hidden">
                      <img src={mediaUrl(r.featured_image) || PLACEHOLDER} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </div>
                    <div className="p-5">
                      <span className="text-xs text-[#0089D0] font-semibold">{r.category}</span>
                      <h3 className="text-lg font-bold text-[#003057] mt-2 group-hover:text-[#0089D0]">{r.title}</h3>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ArticleDetail;
